import type { LoopRange } from '../project/types';

/**
 * Transport — the master clock.
 *
 * Uses the classic "lookahead scheduler" pattern (Chris Wilson, "A Tale of
 * Two Clocks"): a cheap setInterval wakes every ~25 ms and schedules all
 * steps falling within the next 120 ms against the AudioContext clock.
 * All timing math derives from AudioContext.currentTime, so there is no
 * drift between what is scheduled and what the UI displays.
 *
 * Position model: absolute step index, 1 step = one 16th note, 16 steps/bar.
 */
export class Transport {
  playing = false;

  constructor(private readonly time: () => number) {}

  private bpm = 120;
  private stepsPerBar = 16;
  private beatsPerBar = 4;
  private loop: LoopRange = { enabled: true, startStep: 0, endStep: 16 };

  /** ctx time at which the current position anchor begins. */
  private anchorTime = 0;
  /** step at anchorTime. */
  private anchorStep = 0;
  /** next step to hand to the scheduler. */
  private nextStep = 0;
  /** ctx time of nextStep. */
  private nextStepTime = 0;

  private timer: number | null = null;
  private readonly scheduleAhead = 0.12;
  private readonly tickMs = 25;

  /** Called for every step scheduled; `time` is the exact ctx time to fire at. */
  onStep: ((step: number, time: number) => void) | null = null;

  get stepsPerSecond(): number {
    return (this.bpm / 60) * 4;
  }

  get currentBpm(): number {
    return this.bpm;
  }

  get loopRange(): LoopRange {
    return this.loop;
  }

  setBpm(bpm: number): void {
    const clamped = Math.min(300, Math.max(30, bpm));
    if (clamped === this.bpm) return;
    if (this.playing) {
      // Re-anchor so the current position is preserved across the tempo change.
      const pos = this.positionStep;
      this.bpm = clamped;
      this.anchorStep = Math.floor(pos);
      this.anchorTime = this.now();
      this.nextStep = Math.ceil(pos);
      this.nextStepTime = this.now();
    } else {
      this.bpm = clamped;
    }
  }

  setLoop(loop: LoopRange): void {
    this.loop = { ...loop };
    if (!this.playing) {
      this.anchorStep = loop.enabled ? loop.startStep : this.anchorStep;
    }
  }

  setMeter(stepsPerBar: number, beatsPerBar: number): void {
    this.stepsPerBar = Math.max(4, stepsPerBar);
    this.beatsPerBar = Math.max(1, beatsPerBar);
  }

  get currentStepsPerBar(): number {
    return this.stepsPerBar;
  }

  /** Fractional step position right now (for UI playheads/readouts). */
  get positionStep(): number {
    if (!this.playing) return this.anchorStep;
    return this.anchorStep + (this.now() - this.anchorTime) * this.stepsPerSecond;
  }

  /**
   * Prepare to start: sets the position anchor and returns the exact ctx
   * time at which the sequence will begin. `preRoll` seconds of lead time
   * let the caller schedule count-in clicks before the sequence starts.
   */
  planStart(fromStep?: number, preRoll = 0.08): number {
    const start = fromStep ?? (this.loop.enabled ? this.loop.startStep : this.anchorStep);
    this.anchorStep = start;
    this.anchorTime = this.now() + preRoll;
    this.nextStep = start;
    this.nextStepTime = this.anchorTime;
    return this.anchorTime;
  }

  /** Begin playback after planStart(). */
  start(): void {
    if (this.playing) return;
    this.playing = true;
    this.runScheduler();
  }

  play(fromStep?: number): void {
    if (this.playing) return;
    this.planStart(fromStep);
    this.start();
  }

  stop(): void {
    if (!this.playing) return;
    this.playing = false;
    this.clearTimer();
    this.anchorStep = this.loop.enabled ? this.loop.startStep : 0;
  }

  /** Jump to an absolute step. While playing, scheduling resumes from there. */
  seek(step: number): void {
    const clamped = Math.max(0, Math.floor(step));
    this.anchorStep = clamped;
    if (this.playing) {
      this.anchorTime = this.now();
      this.nextStep = clamped;
      this.nextStepTime = this.now();
    }
  }

  /** Position in bars.beat.sixteenth for display, 1-based. */
  get positionDisplay(): { bar: number; beat: number; step: number } {
    const s = Math.max(0, Math.floor(this.positionStep));
    const spb = this.stepsPerBar;
    const stepsPerBeat = spb / this.beatsPerBar;
    return {
      bar: Math.floor(s / spb) + 1,
      beat: Math.floor((s % spb) / stepsPerBeat) + 1,
      step: (s % 4) + 1,
    };
  }

  private now(): number {
    return this.time();
  }

  private advance(): void {
    let next = this.nextStep + 1;
    if (this.loop.enabled && next >= this.loop.endStep) {
      next = this.loop.startStep;
    } else if (next >= 16384) {
      next = 0; // safety cap until the playlist takes over
    }
    this.nextStep = next;
    this.nextStepTime += 1 / this.stepsPerSecond;
  }

  private runScheduler(): void {
    this.clearTimer();
    this.tick();
    this.timer = window.setInterval(() => this.tick(), this.tickMs);
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  private tick(): void {
    if (!this.playing) return;
    const horizon = this.now() + this.scheduleAhead;
    while (this.nextStepTime < horizon) {
      this.onStep?.(this.nextStep, this.nextStepTime);
      this.advance();
    }
  }
}